/**
 * EditorOutline — Live heading outline extracted from BlockNote blocks.
 * Replaces the markdown-parsing approach with direct block inspection.
 * Includes click-to-scroll and active heading tracking via IntersectionObserver.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FloatingOutline, type OutlineItem } from '../../requirements-editor-v2/outline/FloatingOutline';
import type { BlockNoteEditor as BlockNoteEditorCore } from '@blocknote/core';

interface EditorOutlineProps {
  editor: BlockNoteEditorCore | null;
  className?: string;
}

/**
 * Extract outline items directly from BlockNote blocks (not markdown).
 * Uses stable block IDs for reliable click-to-scroll navigation.
 */
function extractOutlineFromBlocks(editor: BlockNoteEditorCore): OutlineItem[] {
  return editor.document
    .filter(block => block.type === 'heading')
    .map(block => ({
      id: block.id,
      level: (block.props as { level: number }).level,
      text: Array.isArray(block.content)
        ? (block.content as Array<{ type: string; text?: string }>)
            .filter(c => c.type === 'text')
            .map(c => c.text || '')
            .join('')
        : '',
    }))
    .filter(item => item.text.length > 0);
}

export function EditorOutline({ editor, className }: EditorOutlineProps) {
  const [outlineItems, setOutlineItems] = useState<OutlineItem[]>([]);
  const [activeItemId, setActiveItemId] = useState<string | undefined>();
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Rebuild outline when editor content changes
  useEffect(() => {
    if (!editor) return;

    const update = () => {
      setOutlineItems(extractOutlineFromBlocks(editor));
    };

    // Initial extraction
    update();

    // Subscribe to content changes
    const unsubscribe = editor.onEditorContentChange(update);
    return () => unsubscribe?.();
  }, [editor]);

  // Active heading tracking via IntersectionObserver
  useEffect(() => {
    if (!editor || outlineItems.length === 0) return;

    // Clean up previous observer
    observerRef.current?.disconnect();

    const headingElements = outlineItems
      .map(item => document.querySelector(`[data-id="${item.id}"]`))
      .filter(Boolean) as Element[];

    if (headingElements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the topmost visible heading
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const blockId = entry.target.getAttribute('data-id');
            if (blockId) {
              setActiveItemId(blockId);
              break;
            }
          }
        }
      },
      { rootMargin: '-10% 0px -80% 0px' }
    );

    headingElements.forEach(el => observer.observe(el));
    observerRef.current = observer;

    return () => observer.disconnect();
  }, [editor, outlineItems]);

  // Click-to-scroll using BlockNote's cursor API + DOM scrollIntoView
  const handleItemClick = useCallback((itemId: string) => {
    if (!editor) return;

    setActiveItemId(itemId);

    try {
      editor.setTextCursorPosition(itemId, 'start');
    } catch {
      // Block might not exist anymore
    }

    const el = document.querySelector(`[data-id="${itemId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [editor]);

  if (outlineItems.length === 0) return null;

  return (
    <FloatingOutline
      items={outlineItems}
      onItemClick={handleItemClick}
      activeItemId={activeItemId}
      className={className}
    />
  );
}

export default EditorOutline;
